import React, { useContext, useEffect } from 'react';
import t from '@jetshop/intl';
import styled, { css } from 'react-emotion';
import { Route, Switch } from 'react-router-dom';
import CategoryHeader from './CategoryHeader';
import { ProductGrid } from './ProductGrid';
import PaginationContext from '@jetshop/core/components/Pagination/PaginationContext';
import { PaginationBehaviour } from '@jetshop/ui/Pagination';
import { PaginationWrapper } from '@jetshop/ui/Pagination/Pagination';
import PageLinks from '../Pagination/PageLinks';
import PaginationInfo from '../Pagination/PaginationInfo';
import Pagination from '../Pagination/Pagination';

import BlogPage from '../BlogPage/BlogPage';
import Layout from '../Layout/Layout';

import CategoryPlaceholder from './CategoryPlaceholder';

const CategoryContent = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const PerPage = styled.button`
  display: flex;
  font-weight: bold;
  align-self: center;
  margin: 10px auto 20px auto;
  background: none;
  border: none;
  outline: none;

  p:after {
    content: '';
    width: 0px;
    height: 1px;
    display: block;
    background: ${({ theme }) => theme.colors.primary};
    transition: 300ms;
  }

  &:hover {
    p:after {
      width: 100%;
    }
  }
`;

export const paginationStyles = css`
  a {
    padding: 1em;
    color: #2f80ed;
    :hover {
      opacity: 0.8;
    }
    :first-of-type {
      padding-left: 0px;
    }
    :last-of-type {
      padding-right: 0px;
    }
  }
  a[data-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    color: #777;
  }
`;

const Category = ({ category, cacheHit, result: { loading, data } }) => {
  const { currentPage, setProductsPerPage, perPage } = useContext(
    PaginationContext
  );
  useEffect(() => {
    if (perPage < 40) setProductsPerPage(40);
  }, []);

  if (!cacheHit) {
    return <CategoryPlaceholder rows={3} products={4} />;
  }

  const { primaryRoute } = category;
  const breadcrumbProps = primaryRoute
    ? {
        parents: primaryRoute.parents,
        breadcrumbText: category.breadcrumbText
      }
    : null;

  return (
    <Switch>
      <Route
        path="/att-resa-med-barn"
        render={props => <BlogPage {...props} category={category} />}
      />
      <Route
        path="/reisile-lastega-blogi"
        render={props => <BlogPage {...props} category={category} />}
      />
      <Route
        path="/lapsiperhe-matkustaa"
        render={props => <BlogPage {...props} category={category} />}
      />
      <Route
        render={props => (
          <Layout
            breadcrumbs={breadcrumbProps}
            category={category}
            route={data.route}
          >
            {currentPage === 1 && <CategoryHeader category={category} />}
            <PaginationContext.Consumer>
              {({ offset, ...props }) => (
                <>
                  <PaginationBehaviour
                    offset={offset}
                    perPage={perPage}
                    total={category.products.totalResults}
                    {...props}
                  >
                    {props => (
                      <PaginationWrapper>
                        <PaginationInfo
                          offset={offset}
                          perPage={perPage}
                          {...props}
                        />
                        <PageLinks currentPage={currentPage} {...props} />
                      </PaginationWrapper>
                    )}
                  </PaginationBehaviour>

                  <CategoryContent>
                    <ProductGrid
                      products={category.products.result}
                      listName={category.name}
                      categoryPath={
                        category.isDynamic ? null : data && data.route
                      }
                      loading={loading}
                      imageSizes={[3 / 2, 1 / 2, 1 / 2, 1 / 4]}
                    />
                  </CategoryContent>

                  <PaginationBehaviour
                    offset={offset}
                    perPage={perPage}
                    total={category.products.totalResults}
                    {...props}
                  >
                    {props => (
                      <>
                        <PaginationWrapper>
                          <Pagination {...props} className={paginationStyles} />
                        </PaginationWrapper>
                        <PerPage
                          onClick={() =>
                            setProductsPerPage(perPage === 40 ? 100 : 40)
                          }
                        >
                          <p>
                            {perPage === 40 ? 100 : 40} {t('per page')}
                          </p>
                        </PerPage>
                      </>
                    )}
                  </PaginationBehaviour>
                </>
              )}
            </PaginationContext.Consumer>
          </Layout>
        )}
      />
    </Switch>
  );
};

export default Category;
